import React, { useState, useEffect } from 'react';
//@ts-ignore
import HorizontalTimeline from 'react-horizontal-timeline';
import AttributeRow from '../../attributeRow';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6';
import EmptyRequestDisplay from '../AssetHubNoData';
import Loader from 'components/Loader';
import type { AssetInspectionRecord } from 'actions/asset';
import type { MergedInspectionReport } from 'customHooks/useFetchInspectionReports';
import { formatCurrency } from '../../../../utils/converter';
import {
  findInspectionTimelineIndex,
  type InspectionSelection,
} from '../assetHubInspectionSelection';

interface InspectionsSummaryReportProps {
  assetId: number;
  assetHubRefreshToken: number;
  selectedInspection?: InspectionSelection | null;
  onInspectionSelect?: React.Dispatch<
    React.SetStateAction<InspectionSelection | null>
  >;
  readonly inspectionRecords?: readonly AssetInspectionRecord[];
  readonly recordsLoading?: boolean;
  readonly recordsError?: boolean;
  readonly inspectionReports: readonly MergedInspectionReport[];
  readonly reportsLoading: boolean;
  readonly reportsError: boolean;
}

const PAGE_SIZE = 1;

const InspectionsSummaryReport: React.FC<InspectionsSummaryReportProps> = ({
  assetId,
  selectedInspection,
  onInspectionSelect,
  recordsLoading = false,
  recordsError = false,
  inspectionReports,
  reportsLoading,
  reportsError,
}) => {
  const [mergedInspections, setMergedInspections] = useState<
    MergedInspectionReport[]
  >([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedElement, setSelectedElement] = useState<string | 'all'>('all');
  const [selectedSpanIndex, setSelectedSpanIndex] = useState<number>(0);

  useEffect(() => {
    setMergedInspections([...inspectionReports]);
  }, [inspectionReports]);

  useEffect(() => {
    if (mergedInspections.length === 0) return;

    if (selectedInspection) {
      const foundIndex = findInspectionTimelineIndex(
        mergedInspections,
        selectedInspection,
      );
      if (foundIndex >= 0) {
        setSelectedIndex(foundIndex);
        return;
      }
      return;
    }

    setSelectedIndex(mergedInspections.length - 1);
  }, [selectedInspection, mergedInspections]);

  useEffect(() => {
    if (!selectedInspection) return;
    setCurrentPage(1);
    setSelectedSpanIndex(0);
    setSelectedElement('all');
  }, [selectedInspection]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedSpanIndex(0);
    setSelectedElement('all');
  }, [selectedIndex]);

  if (recordsLoading || reportsLoading) return <Loader />;
  if (!assetId) return <p>No asset selected.</p>;
  if (recordsError || reportsError)
    return <p>Error loading inspection data.</p>;
  if (inspectionReports.length === 0)
    return (
      <div className="empty_request--container">
        <EmptyRequestDisplay />
      </div>
    );

  const selectedMerged = mergedInspections[selectedIndex];

  const allSpans = Array.from(
    new Set(
      (selectedMerged?.observations || []).map((obs) => obs.span),
    ),
  ).sort((a, b) => a - b);

  const safeSpanIndex =
    allSpans.length === 0
      ? 0
      : Math.min(selectedSpanIndex, allSpans.length - 1);
  const selectedSpan = allSpans[safeSpanIndex];

  const allElements = Array.from(
    new Set(
      (selectedMerged?.observations || [])
        .filter(
          (obs) => selectedSpan === undefined || obs.span === selectedSpan,
        )
        .map((obs) => obs.element),
    ),
  ).sort();

  const filteredObservations =
    selectedMerged?.observations?.filter((obs) => {
      const spanMatch = selectedSpan === undefined || obs.span === selectedSpan;
      const elementMatch =
        selectedElement === 'all' || obs.element === selectedElement;
      return spanMatch && elementMatch;
    }) || [];

  const totalPages = Math.ceil(filteredObservations.length / PAGE_SIZE);
  const paginatedObservation = filteredObservations.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  )[0];

  if (mergedInspections.length === 0) {
    return (
      <div className="empty_request--container">
        <EmptyRequestDisplay />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <div className="xa_asset_inspection_records--container">
        <div className="xa_asset_inspection_records">
          <div className="xa_asset_inspection_records--header">
            <HorizontalTimeline
              key={`inspection-timeline-${assetId}`}
              index={selectedIndex}
              indexClick={(index: number) => {
                setSelectedIndex(index);
                const inspection = mergedInspections[index];
                if (inspection && onInspectionSelect) {
                  onInspectionSelect({
                    inspectionType: inspection.inspectionType,
                    recordId: inspection.id,
                  });
                }
              }}
              values={mergedInspections.map((mi) => mi.inspectionDate)}
              isKeyboardEnabled
              isTouchEnabled
            />
          </div>

          {selectedMerged ? (
            <div className="xa_asset_inspection_records--data">
              <div className="xa_asset_inspection_report--scrollable-content">
                <div className="sticky-filters">
                  {allSpans.length > 0 ? (
                    <div className="filter-block">
                      <HorizontalTimeline
                        key={`span-timeline-${assetId}-${selectedMerged.id}`}
                        values={allSpans.map((span) => `Span ${span}`)}
                        index={safeSpanIndex}
                        indexClick={(index: number) => {
                          setSelectedSpanIndex(index);
                          setCurrentPage(1);
                        }}
                        getLabel={(label: string) => label}
                        isKeyboardEnabled
                        isTouchEnabled
                      />
                    </div>
                  ) : null}

                  {allElements.length > 0 ? (
                    <div
                      className="xa-element-selector"
                      style={{ marginBottom: '1rem' }}
                    >
                      <label
                        htmlFor="element-select"
                        style={{ marginRight: '0.5rem' }}
                      >
                        Filter by Element:
                      </label>
                      <select
                        id="element-select"
                        value={selectedElement}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedElement(value === 'all' ? 'all' : value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="all">All</option>
                        {allElements.map((el) => (
                          <option key={el} value={el}>
                            {el}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>

                <div className="observation-results">
                  {filteredObservations.length > 0 && paginatedObservation ? (
                    <>
                      {selectedElement === 'all' &&
                      paginatedObservation.element != null ? (
                        <AttributeRow
                          name="Element"
                          value={String(paginatedObservation.element)}
                          key="element"
                        />
                      ) : null}
                      {Object.entries(paginatedObservation)
                        .filter(
                          ([key]) =>
                            key !== 'span' &&
                            key !== 'element' &&
                            key !== 'id',
                        )
                        .map(([key, value]) => {
                          let formattedValue: string;
                          switch (key) {
                            case 'workCost':
                              formattedValue = formatCurrency(value as number);
                              break;
                            case 'priority':
                              formattedValue =
                                value === '?' ? 'N/A' : String(value);
                              break;
                            case 'comments':
                            case 'action':
                            case 'suggestedTreatment':
                              formattedValue =
                                value === null ? 'N/A' : String(value);
                              break;
                            default:
                              formattedValue = String(value);
                          }
                          return (
                            <AttributeRow
                              name={key}
                              value={formattedValue}
                              key={key}
                            />
                          );
                        })}
                    </>
                  ) : (
                    <AttributeRow name="Observations" value="None" />
                  )}
                </div>
              </div>

              {totalPages > 1 ? (
                <div className="xa_asset_inspection_report--pagination">
                  <FaAngleLeft
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    style={{
                      cursor: currentPage === 1 ? 'auto' : 'pointer',
                      opacity: currentPage === 1 ? '0.2' : '1',
                    }}
                  />
                  <span style={{ fontSize: '1.2rem' }}>
                    Page <b>{currentPage}</b> of {totalPages}
                  </span>
                  <FaAngleRight
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    style={{
                      cursor: currentPage === totalPages ? 'auto' : 'pointer',
                      opacity: currentPage === totalPages ? '0.2' : '1',
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default InspectionsSummaryReport;
